import * as PIXI from 'pixi.js';
import { StoryManager } from '@/systems';
// import {dayConfigs} from "@/config/DayConfig"; 

export class DayDisplay {
  private container: PIXI.Container;
  private background: PIXI.Sprite;
  private fallbackBackground: PIXI.Graphics;
  private dayText: PIXI.Text;
  // private nameText: PIXI.Text; 
  private descriptionText: PIXI.Text;
  private storyManager: StoryManager;
  private dayIndicator:PIXI.Sprite;

  private dayIconTextures!: {day:PIXI.Texture, sunrise:PIXI.Texture, sunset:PIXI.Texture, night:PIXI.Texture};

  constructor() {
    this.storyManager = StoryManager.getInstance();
    this.container = new PIXI.Container();

    // Create fallback background first to ensure visibility
    this.fallbackBackground = new PIXI.Graphics();
    this.fallbackBackground.beginFill(0x000000, 0.8);
    this.fallbackBackground.drawRoundedRect(0, 0, 550, 150, 10);
    this.fallbackBackground.endFill();

    // Create placeholder sprite initially
    this.background = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.background.width = 800;
    this.background.height = 150;

    this.dayIndicator = new PIXI.Sprite(PIXI.Texture.EMPTY);
    this.dayIndicator.width = 50;
    this.dayIndicator.height = 50;
    this.dayIndicator.anchor.set(0.5, 0.5);
    this.dayIndicator.x = 50;
    this.dayIndicator.y = 75;

    // Create day number text
    this.dayText = new PIXI.Text('Day 1', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
      align: 'left',
      padding: 4,
    });
    this.dayText.resolution = window.devicePixelRatio || 2;
    this.dayText.anchor.set(0, 1); // Left aligned, top aligned
    this.dayText.x = 100;
    this.dayText.y = 60; // 40px top padding + 10px

    // Create day name text
    // this.nameText = new PIXI.Text('First Contact', {
    //   fontFamily: 'Arial',
    //   fontSize: 16,
    //   fill: 0xffdd44,
    //   fontWeight: 'bold',
    //   align: 'right',
    // });
    // this.nameText.anchor.set(1, 0); // Right aligned, top aligned
    // this.nameText.x = 400
    // this.nameText.y = 75; // 40px top padding + 40px for day text

    // Create description text
    this.descriptionText = new PIXI.Text('', {
      fontFamily: 'Rosarivo, serif',
      fontSize: 24,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: 700,
      align: 'left',
      padding: 4,
    });
    this.descriptionText.resolution = window.devicePixelRatio || 2;
    this.descriptionText.anchor.set(0, 0);
    this.descriptionText.x = 100;
    this.descriptionText.y = 75; // 40px top padding + 65px for previous text

    // Add fallback first, then sprite
    this.container.addChild(this.fallbackBackground);
    this.container.addChild(this.background);
    this.container.addChild(this.dayText);
    // this.container.addChild(this.nameText);
    this.container.addChild(this.descriptionText);
    this.container.addChild(this.dayIndicator)
    
    // Ensure visibility for debugging
    this.container.visible = true;
    console.log('DayDisplay initialized with container size:', this.container.width, 'x', this.container.height);

    // Initial position will be updated by updatePosition
    this.container.x = 0;
    this.container.y = 0; // Remove spacing from top edge

    // Listen for day changes
    this.storyManager.addDayEventListener('dayChanged', () => {
      this.updateDisplay();
    });

    this.initialization()

  }

  private async initialization(){
    await this.initializeBackgroundTexture();

    // Initial update
    await this.setupDayIndicator()
    await this.updateDisplay();
  }

  private async setupDayIndicator() {
    const textureSunrise = await PIXI.Assets.load('/assets/misc/icon-sunrise.png');
    const textureDay = await PIXI.Assets.load('/assets/misc/icon-daytime.png');
    const textureSunset = await PIXI.Assets.load('/assets/misc/icon-sunset.png');
    const textureNight = await PIXI.Assets.load('/assets/misc/icon-night.png');

    this.dayIconTextures = {
      sunrise : textureSunrise,
      day : textureDay,
      sunset : textureSunset,
      night : textureNight,
    }

  }

  private async initializeBackgroundTexture(): Promise<void> {
    // gradient
    const canvas = document.createElement('canvas');
    canvas.width = 600; 
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Gradient #493E2A to Transparent
      const gradient = ctx.createLinearGradient(0, 0, 600, 0);
      gradient.addColorStop(0, '#493E2A');
      gradient.addColorStop(0.4, '#493E2A'); // Solid for a bit
      gradient.addColorStop(1, 'rgba(73, 62, 42, 0)'); // Fade out
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 600, 175);
      
      const texture = PIXI.Texture.from(canvas);
      this.background.texture = texture;
      this.background.width = 600;
      this.background.height = 175;
    }
    
    this.background.visible = true;
    this.fallbackBackground.visible = false;
  }

  private updateDisplay(): void {
    const currentDay = this.storyManager.getCurrentDay();
    const dayInfo = this.storyManager.getDayInfo(currentDay);


    if (dayInfo) {

      switch (dayInfo.dayIcon){
        case "sunrise":
          this.dayIndicator.texture = this.dayIconTextures.sunrise
          break;
        case "day":
          this.dayIndicator.texture = this.dayIconTextures.day
          break;
        case "sunset":
          this.dayIndicator.texture = this.dayIconTextures.sunset
          break;
        case "night":
          this.dayIndicator.texture = this.dayIconTextures.night
          break;
        default:
          this.dayIndicator.texture = PIXI.Texture.EMPTY
      }

      if(dayInfo.dayString){
        this.dayText.text = dayInfo.dayString;
      }else{
        this.dayText.text = `Day ${currentDay}`;
      }
      // this.nameText.text = dayInfo.name;
      this.descriptionText.text = dayInfo.description || '';
    } else {
      this.dayText.text = `Day ${currentDay}`;
      // this.nameText.text = 'Unknown';
      this.descriptionText.text = '';
    }

    // Background size will be determined by the image asset
    // The text content will adapt to the background image dimensions
  }

  public updatePosition(screenWidth: number, _screenHeight: number): void {
    // Dynamic scaling based on reference resolution (e.g. 1280x720)
    // On mobile landscape (812x375) smaller UI
    const referenceWidth = 1280;
    const minScale = 0.4;
    const maxScale = 0.7;
    
    let scale = screenWidth / referenceWidth;
    scale = Math.max(minScale, Math.min(maxScale, scale));
    
    this.container.scale.set(scale);

    // Align to Left
    this.container.x = 0;
    this.container.y = 30;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public show(): void {
    this.container.visible = true;
  }

  public hide(): void {
    this.container.visible = false;
  }

  public setVisible(visible: boolean): void {
    this.container.visible = visible;
  }

  public destroy(): void {
    this.container.destroy();
  }
}
